import type { FileClassificationStatus, FileId, UploadedFile } from "@inrev/common";
import { useCallback, useMemo, useRef } from "react";
import { isClassificationPendingStatus } from "./classification-poll";
import { FileClassificationSubscriber } from "./FileClassificationSubscriber";

type ClassificationFields = {
	types?: readonly unknown[];
	classificationStatus?: FileClassificationStatus;
	typesConfirmedByAdmin?: boolean;
};

type ClassifiableFile = ClassificationFields & {
	id: FileId;
};

type AttachmentWithClassifiableFile = {
	id: string;
	createdAtMs: number;
	file: ClassifiableFile;
};

type ClassificationAccessor<T> = {
	getFile: (item: T) => ClassificationFields;
	getFileId: (item: T) => FileId;
	mergeUpdatedFile: (item: T, updated: UploadedFile) => T;
};

export const needsClassificationWatch = (file: ClassificationFields): boolean =>
	(file.types?.length ?? 0) === 0 &&
	(file.classificationStatus === undefined ||
		isClassificationPendingStatus(file.classificationStatus));

export const classificationFieldsEqual = (
	current: ClassificationFields,
	next: ClassificationFields,
): boolean =>
	current.classificationStatus === next.classificationStatus &&
	(current.types ?? []).join(",") === (next.types ?? []).join(",") &&
	current.typesConfirmedByAdmin === next.typesConfirmedByAdmin;

export const createUploadedFileClassificationAccessor = <
	T extends UploadedFile = UploadedFile,
>(): ClassificationAccessor<T> => ({
	getFile: (file) => file,
	getFileId: (file) => file.id,
	mergeUpdatedFile: (existing, updated) => ({ ...existing, ...updated, id: existing.id }) as T,
});

export const createAttachmentFileClassificationAccessor = <
	T extends AttachmentWithClassifiableFile = AttachmentWithClassifiableFile,
>(): ClassificationAccessor<T> => ({
	getFile: (attachment) => attachment.file,
	getFileId: (attachment) => attachment.file.id,
	mergeUpdatedFile: (existing, updated) =>
		({
			...existing,
			file: { ...existing.file, ...updated, id: existing.file.id },
		}) as T,
});

export const attachmentFileClassificationAccessor = createAttachmentFileClassificationAccessor();

export const pendingClassificationFileIds = <T,>(
	items: readonly T[],
	accessor: ClassificationAccessor<T>,
): FileId[] =>
	items
		.filter((item) => needsClassificationWatch(accessor.getFile(item)))
		.map((item) => accessor.getFileId(item));

export const applyClassificationUpdate = <T,>(
	items: readonly T[],
	fileId: FileId,
	updatedFile: UploadedFile,
	accessor: ClassificationAccessor<T>,
): T[] | undefined => {
	const { getFile, getFileId, mergeUpdatedFile } = accessor;
	let changed = false;
	const next = items.map((existing) => {
		if (getFileId(existing) !== fileId) return existing;
		if (classificationFieldsEqual(getFile(existing), updatedFile)) return existing;
		changed = true;
		return mergeUpdatedFile(existing, updatedFile);
	});
	return changed ? next : undefined;
};

export const useClassificationSync = <T,>(
	items: readonly T[],
	onItemsChange: (next: T[]) => void,
	accessor: ClassificationAccessor<T>,
) => {
	const accessorRef = useRef(accessor);
	accessorRef.current = accessor;

	const pendingClassificationIds = useMemo(
		() => pendingClassificationFileIds(items, accessorRef.current),
		[items],
	);

	const itemsRef = useRef(items);
	itemsRef.current = items;
	const onItemsChangeRef = useRef(onItemsChange);
	onItemsChangeRef.current = onItemsChange;

	const handleClassificationUpdate = useCallback((fileId: FileId, updatedFile: UploadedFile) => {
		const next = applyClassificationUpdate(
			itemsRef.current,
			fileId,
			updatedFile,
			accessorRef.current,
		);
		if (next) onItemsChangeRef.current(next);
	}, []);

	return useClassificationWatchers(pendingClassificationIds, handleClassificationUpdate);
};

export const useClassificationWatchers = (
	pendingFileIds: readonly FileId[],
	onUpdate: (fileId: FileId, file: UploadedFile) => void,
) =>
	useMemo(
		() =>
			pendingFileIds.map((fileId) => (
				<FileClassificationSubscriber
					key={`classification-watch-${fileId}`}
					fileId={fileId}
					onUpdate={onUpdate}
				/>
			)),
		[pendingFileIds, onUpdate],
	);
